/**
 * usePageMeta — light-weight per-page SEO/OG/canonical injector.
 * Sets <title>, <meta description>, OG/Twitter, and canonical via DOM mutation
 * on mount; restores nothing on unmount (the next page just overwrites).
 *
 * Modern crawlers (Google, Bing, Cloudflare bots) execute JS and respect these.
 * Static OG defaults still live in public/index.html for non-JS scrapers.
 */
import { useEffect } from "react";

const SITE = "Cybersecurity Forefront";
const SITE_DOMAIN = "https://cybersecurityforefront.com";
const DEFAULT_OG = `${SITE_DOMAIN}/og-hero-image.png`;

function setMeta(name, content, attr = "name") {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function setJsonLd(id, json) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(json);
}

export function usePageMeta({
    title,
    description,
    canonical,
    ogImage = DEFAULT_OG,
    ogType = "website",
    jsonLd,
}) {
    useEffect(() => {
        const fullTitle = title ? `${title} | ${SITE}` : SITE;
        document.title = fullTitle;
        setMeta("description", description);
        setMeta("og:title", fullTitle, "property");
        setMeta("og:description", description, "property");
        setMeta("og:image", ogImage, "property");
        setMeta("og:type", ogType, "property");
        // Always resolve canonical to the production apex domain (never the
        // preview hostname) so that Helmet + usePageMeta agree on the URL.
        const url =
            canonical ||
            (typeof window !== "undefined"
                ? `${SITE_DOMAIN}${window.location.pathname}`
                : "");
        if (url) setMeta("og:url", url, "property");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", fullTitle);
        setMeta("twitter:description", description);
        setMeta("twitter:image", ogImage);
        if (url) setLink("canonical", url);
        if (jsonLd) setJsonLd("page-jsonld", jsonLd);
    }, [title, description, canonical, ogImage, ogType, jsonLd]);
}

export default usePageMeta;
